/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

interface ShowcaseBannerProps {
    bannerUrl: string;
    fallbackText: string;
    size?: 'auto' | 'contain' | 'cover' | 'initial' | 'inherit' | 'unset';
}

/**
 * ShowcaseBanner — displays an image banner.
 * If the image cannot be loaded the provided `fallbackText` is shown centered.
 *
 * @param {string} bannerUrl - Image URL.
 * @param {string} [fallbackText] - Text to show when the image is unavailable.
 * @param size - The CSS background-size property value (default is 'cover').
 */
const ShowcaseBanner: FunctionComponent<ShowcaseBannerProps> = ({
    bannerUrl,
    fallbackText,
    size,
}) => {
    const showcaseBannerRef = useRef<HTMLDivElement>(null);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let isMounted = true;

        // If no URL provided, treat as an error (show fallback)
        if (!bannerUrl) {
            setHasError(true);
            if (showcaseBannerRef.current) showcaseBannerRef.current.style.backgroundImage = '';
            return;
        }

        const img = new Image();
        img.onload = () => {
            if (!isMounted) return;
            setHasError(false);
            if (showcaseBannerRef.current) {
                showcaseBannerRef.current.style.backgroundImage = `url("${bannerUrl}")`;
                showcaseBannerRef.current.style.setProperty('background-size', size || 'cover');
            }
        };
        img.onerror = () => {
            if (!isMounted) return;
            setHasError(true);
            if (showcaseBannerRef.current) showcaseBannerRef.current.style.backgroundImage = '';
        };
        img.src = bannerUrl;

        // eslint-disable-next-line consistent-return
        return () => {
            isMounted = false;
            img.onload = null;
            img.onerror = null;
        };
    }, [bannerUrl, size]);

    return (
        <React.Fragment>
            <div className="uikit showcase-banner" ref={showcaseBannerRef}>
                {hasError && fallbackText ? (
                    <span className="uikit showcase-banner-fallback primary-text-style text-size-xlarge">
                        {fallbackText}
                    </span>
                ) : null}
            </div>
        </React.Fragment>
    );
};

export default ShowcaseBanner;
